import type {
  MembershipRoleChoiceResponse,
  OrganizationRoleResponse,
} from "@kontextwerk/control-web-client"

export type RoleKind = OrganizationRoleResponse["kind"]

/** Role facts available for presentation; `kind` is null when only a name is known. */
export type RoleReference = Readonly<{ id: string; name: string; kind: RoleKind | null }>

const ROLE_KIND_LABELS: Record<RoleKind, string> = {
  owner: "Inhaber",
  administrator: "Administrator",
  employee: "Mitarbeiter",
  custom: "Eigene Rolle",
}

export const roleKindLabel = (kind: RoleKind): string => ROLE_KIND_LABELS[kind]

/**
 * Display lookup by role id. Full roles need role read access; assignable choices and the
 * actor's own confirmed context role are fallbacks. A label never grants or denies anything.
 */
export const buildRoleIndex = ({
  roles,
  choices,
  ownRole,
}: {
  roles: readonly OrganizationRoleResponse[]
  choices: readonly MembershipRoleChoiceResponse[]
  ownRole: Readonly<{ id: string; name: string }> | null
}): ReadonlyMap<string, RoleReference> => {
  const index = new Map<string, RoleReference>()

  if (ownRole)
    index.set(ownRole.id.toLowerCase(), { id: ownRole.id, name: ownRole.name, kind: null })

  for (const choice of choices)
    index.set(choice.id.toLowerCase(), { id: choice.id, name: choice.name, kind: choice.kind })

  for (const role of roles)
    index.set(role.id.toLowerCase(), { id: role.id, name: role.name, kind: role.kind })

  return index
}

export const findRole = (
  index: ReadonlyMap<string, RoleReference>,
  roleId: string | null | undefined,
): RoleReference | null => (roleId ? (index.get(roleId.toLowerCase()) ?? null) : null)

/** System roles show their German kind label, own roles their stored name. */
export const roleLabel = (role: RoleReference | null | undefined): string | null => {
  if (!role) return null

  return role.kind && role.kind !== "custom" ? ROLE_KIND_LABELS[role.kind] : role.name
}

/** Number of roster members per role id; members without a known role are not counted. */
export const countMembersByRole = (
  members: readonly Readonly<{ role_id?: string | null }>[],
): ReadonlyMap<string, number> => {
  const counts = new Map<string, number>()
  for (const member of members) {
    const roleId = member.role_id?.toLowerCase()

    if (roleId) counts.set(roleId, (counts.get(roleId) ?? 0) + 1)
  }

  return counts
}
